import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  InputAdornment,
  TextField,
  LinearProgress,
  Typography,
  Link,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import CredentialsInfo from "./CredentialsInfo";
import { downloadReport } from "../../utils/downloadReport";
import RangeDatePicker from "../../../components/Layouts/AppDatePicker/AppDatePicker";
const PaymentDetails = () => {
  const [searchValInput, setSearchValInput] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState<any>();
  const [paymentDetails, setPaymentDetails] = useState<any[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [selected, setSelected] = useState<"credentials" | "transactions">(
    "credentials",
  );
  const [downloading, setDownloading] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const handleDateChange = ([start, end]: [Date | null, Date | null]) => {
    if (start && end && start > end) return;
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const rowsPerPage = 10;
  const pageRange = 5;
  const tableHeaderCellStyle = {
    fontWeight: 600,
    fontSize: "14px",
    color: "#455A64",
    font: "Rubik",
  };
  const tableBodyCellStyle = {
    fontWeight: 400,
    fontSize: "14px",
    color: "#3b3b3b",
    font: "Rubik",
  };
  // Dummy data for tables

  const pageCount = Math.ceil(totalCount / rowsPerPage);
  const handlePageChange = (newPage: number) => {
    const newPageNumber = Math.max(1, Math.min(newPage + 1, pageCount));
    setPage(newPageNumber - 1);
  };

  const navigate = useNavigate();

  const renderPageNumbers = () => {
    const pagesToShow = Math.min(pageCount, pageRange);
    const startPage = Math.max(
      0,
      Math.min(page - Math.floor(pageRange / 2), pageCount - pagesToShow),
    );

    return Array.from({ length: pagesToShow }, (_, index) => {
      const pageNumber = startPage + index + 1;
      return (
        <span
          key={index}
          onClick={() => handlePageChange(pageNumber - 1)}
          className={page === pageNumber - 1 ? "active" : "unactive"}
          style={{ marginLeft: "6px" }}
        >
          {pageNumber}
        </span>
      );
    });
  };
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleCreateuser = () => {
    //   setUserId(uid);
    navigate("/createUser");
  };
  const handleDownloadReport = () => {
    if (!startDate || !endDate) {
      setSnackbarMessage("Please select start and end date");
      setSnackbarSeverity("error");
      setShowErrorSnackbar(true);
      return;
    }

    downloadReport(
      () =>
        ZyppLiteApi.api.downloadTransactionHistoryReportDateWise(
          startDate ? formatDate(startDate) : undefined,
          endDate ? formatDate(endDate) : undefined,
        ),
      `Transaction_History_${formatDate(startDate!)}_to_${formatDate(endDate!)}.xlsx`,
      (msg) => {
        setSnackbarMessage(msg);
        setSnackbarSeverity("success");
        setShowErrorSnackbar(true);
      },
      (msg) => {
        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setShowErrorSnackbar(true);
      },
      setDownloading,
    );
  };
  const fetchUserData = useCallback(() => {
    return ZyppLiteApi.api
      ?.getpaymentDetailList(
        searchValInput,
        page,
        rowsPerPage,
        startDate ? formatDate(startDate) : undefined,
        endDate ? formatDate(endDate) : undefined,
      )
      .then((response: any) => {
        const content = response?.data?.content ?? [];
        const total = response?.data?.totalElements;
        setPaymentDetails(content);
        setTotalCount(total);
      })
      .catch((error: any) => {
        console.error("API call error:", error);
        setPaymentDetails([]);
        setTotalCount(0);
      });
  }, [searchValInput, page, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handlePlanStatusUpdate = (userId: number, planStatus: string) => {
    const payload = {
      userId,
      planStatus,
    };

    ZyppLiteApi.api
      ?.verifyRentalPlan(payload)
      .then((response: any) => {
        if (response?.code === 2000) {
          setSnackbarMessage("Plan status updated successfully");
          setShowErrorSnackbar(true);
          setSnackbarSeverity("success");
          setAnchorEl(null); // Close popper
          setSelectedPlan(null);
          fetchUserData();
        } else {
          setSnackbarMessage(response?.message || "Plan update failed");
          setSnackbarSeverity("error");
          setShowErrorSnackbar(true);
        }
      })
      .catch((error) => {
        console.error("Plan status update error:", error);
        setSnackbarSeverity("error");
        setSnackbarMessage("Plan update failed");
        setShowErrorSnackbar(true);
      });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  return (
    <>
      <div className="flex flex-row justify-center gap-2 py-8">
        <button
          onClick={() => setSelected("credentials")}
          className={`border-2 border-green-600  hover:bg-green-500 hover:text-white  px-4 py-2 rounded-2xl  ${
            selected === "credentials" ? "bg-green-500 text-white" : ""
          }`}
        >
          Credentials Information
        </button>

        <button
          onClick={() => setSelected("transactions")}
          className={`border-2 border-green-600 px-4 hover:bg-green-500 hover:text-white py-2 rounded-2xl ${
            selected === "transactions" ? "bg-green-500 text-white" : ""
          }`}
        >
          Transaction History
        </button>
      </div>

      {selected === "transactions" ? (
        <div className="px-10">
          <div className="w-full flex bg-white min-h-80 box-shadow p-3 rounded-xl">
            <div className="w-full">
              <Grid
                container
                spacing={5}
                className="w-full flex justify-between "
              >
                <Grid item md={3}>
                  <div className="flex ml-4">
                    <h2 className="text-center ml-2 mx-1 mt-2 font-semibold text-2xl text-black pb-2">
                      Payments
                    </h2>
                  </div>
                </Grid>
                <Grid item md={9}>
                  <div className="flex mr-4">
                    <div className="flex w-full justify-end">
                      <div className="mr-3">
                        <TextField
                          placeholder="Search by mobile number"
                          variant="outlined"
                          value={searchValInput}
                          onChange={(e) => {
                            const v = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            setSearchValInput(v);
                          }}
                          type="tel"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            maxLength: 10, // works with tel/text
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                            endAdornment: searchValInput ? (
                              <InputAdornment position="end">
                                <ClearIcon
                                  onClick={() => setSearchValInput("")}
                                  sx={{ cursor: "pointer" }}
                                />
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={{
                            width: 300,
                            marginRight:"10px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "25px",
                              height: 40,
                              
                            },
                          }}
                          error={Boolean(
                            searchValInput && searchValInput.length !== 10,
                          )}
                          helperText={
                            searchValInput && searchValInput.length !== 10
                              ? "Enter 10-digit mobile number"
                              : " "
                          }
                        />
                        
                      </div>
                      <div>
                        <RangeDatePicker
                          startDate={startDate}
                          endDate={endDate}
                          onChange={handleDateChange}
                          minDaysSub={92}
                          maxDaysAdd={0}
                        />
                        </div>
                        <div>

                        <Button
                          onClick={handleDownloadReport}
                          variant="contained"
                          disabled={downloading}
                          sx={{
                            backgroundColor: "#06B681",
                            textTransform: "none",
                          }}
                        >
                          {downloading ? "Downloading..." : "Download"}
                        </Button>
                      </div>

                      <div>
                        <Link
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            handleCreateuser();
                          }}
                        ></Link>
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
              <hr className="my-4" />

              <TableContainer>
                {!paymentDetails ? ( // Check if API response is still undefined (loading)
                  <LinearProgress />
                ) : typeof paymentDetails === "string" ? ( // If API returns "No Data Found" as a string
                  <Typography style={{ textAlign: "center", padding: "10px" }}>
                    No data available
                  </Typography>
                ) : paymentDetails.length === 0 ? ( // If the array is empty
                  <Typography style={{ textAlign: "center", padding: "10px" }}>
                    No data available
                  </Typography>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 120 }}
                        >
                          User Name
                        </TableCell>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 150 }}
                        >
                          Mobile No
                        </TableCell>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 110 }}
                        >
                          Wallet Balance
                        </TableCell>

                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 120 }}
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 120 }}
                        >
                          Payment Mode
                        </TableCell>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 120 }}
                        >
                          Date/Time
                        </TableCell>
                        <TableCell
                          style={{
                            ...tableHeaderCellStyle,
                            textAlign: "center",
                          }}
                          sx={{ minWidth: 120 }}
                        >
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentDetails.map((item: any, index: any) => (
                        <TableRow key={index}>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.userName}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.mobileNo}
                          </TableCell>

                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.walletBalance}
                          </TableCell>

                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                              color:
                                String(item.transactionType) === "CREDIT"
                                  ? "#2e7d32" // green
                                  : "#d32f2f", // red
                              fontWeight: 600,
                            }}
                          >
                            {String(item.transactionType) === "CREDIT"
                              ? "+"
                              : "-"}
                            {item.amount}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.paymentMode}
                          </TableCell>
                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                            }}
                          >
                            {item.paymentDate}
                          </TableCell>

                          <TableCell
                            style={{
                              ...tableBodyCellStyle,
                              textAlign: "center",
                              fontWeight: 700,
                              color: (() => {
                                const v = String(item.paymentStatus || "")
                                  .trim()
                                  .toLowerCase();
                                if (v === "success") return "#2e7d32"; // green
                                if (v === "failure") return "#d32f2f"; // red
                                if (v === "pending") return "#ed6c02"; // orange
                                return undefined; // default color
                              })(),
                            }}
                          >
                            {item.paymentStatus}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>

              <div className="pl-2 pt-4 pb-2 pr-16">
                <div
                  className="pagination"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div>
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      style={{
                        fontFamily: "Rubik",
                        fontSize: "20px",
                        marginRight: "6px",
                      }}
                    >
                      {"<"}
                    </button>
                    {renderPageNumbers()}
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= pageCount - 1}
                      style={{
                        fontFamily: "Rubik",
                        fontSize: "20px",
                        marginLeft: "6px",
                      }}
                    >
                      {">"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Snackbar
            open={showErrorSnackbar}
            autoHideDuration={6000}
            onClose={() => setShowErrorSnackbar(false)}
          >
            <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
          </Snackbar>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
            <DialogTitle>{selectedPlan?.planSelected} Plan</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary">
                Do you want to approve or reject the plan for user:{" "}
                {selectedPlan?.name}?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  if (selectedPlan) {
                    handlePlanStatusUpdate(selectedPlan.userId, "APPROVED");
                  }
                  setOpenDialog(false);
                }}
                variant="contained"
                style={{
                  backgroundColor: "#05B97D",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "20px",
                  padding: "5px 25px",
                }}
              >
                Accept
              </Button>
              <Button
                onClick={() => {
                  if (selectedPlan) {
                    handlePlanStatusUpdate(selectedPlan.userId, "REJECTED");
                  }
                  setOpenDialog(false);
                }}
                variant="contained"
                style={{
                  backgroundColor: "#F94932",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "20px",
                  padding: "5px 25px",
                }}
              >
                Reject
              </Button>
            </DialogActions>
          </Dialog>
         
        </div>
      ) : (
        <CredentialsInfo />
      )}
    </>
  );
};

export default PaymentDetails;
